import React, { useEffect, useRef, useState } from 'react';
import Api from '../../libs/Api';
import NoPhotoIcon from '../../assets/svgs/NoPhotoIcon';
import DownloadIcon from '../../assets/svgs/DownloadIcon'; 
import LoadingSection from '../../components/Loading/LoadingSection';
import translate from '../../libs/i18n';
import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';

const ModelViewerComponent = ({ report }) => {
  const language = useSelector((state) => state.settings.language);

  const [modelUrl, setModelUrl] = useState(null);
  const [isBlack, setIsBlack] = useState(true);
  const [modelBlob, setModelBlob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modelUnavailable, setModelUnavailable] = useState(false);

  const modelViewerRef = useRef();
  const originalColors = useRef(new Map());

  useEffect(() => {
    let objectUrl = null;

    const fetchModel = async () => {
      if (!report?.id) {
        setLoading(false);
        setModelUnavailable(true);
        return;
      }

      try {
        const response = await Api.getGLTFById(report.id, {
          responseType: 'blob',
        });
 
        if (response.status === 200 && response.data) {
          objectUrl = URL.createObjectURL(response.data);
          setModelBlob(response.data);
          setModelUrl(objectUrl);
          setModelUnavailable(false);
        } else {
          setModelUnavailable(true);
        }
      } catch (error) {
        console.error('Erro ao buscar modelo 3D:', error);
        setModelUnavailable(true);
      } finally {
        setLoading(false);
      }
    };

    fetchModel();

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [report]);

const colorEquals = (a, b, tolerance = 0.01) =>
  a.length === b.length && a.every((val, i) => Math.abs(val - b[i]) < tolerance);

const toggleMaterialByColor = (targetColor) => {
  const viewer = modelViewerRef.current;
  if (!viewer || !viewer.model) return;

  const materials = viewer.model.materials;

  for (const material of materials) {
    const baseColor = material?.pbrMetallicRoughness?.baseColorFactor;
    if (!baseColor) continue;

    if (colorEquals(baseColor, targetColor) || colorEquals(originalColors.current.get(material) ?? [], targetColor)) {
      const isHidden = baseColor[3] === 0;

      if (isHidden) {
        // Restaurar cor original
        const original = originalColors.current.get(material);
        if (original) {
          material.setAlphaMode("OPAQUE");
          material.pbrMetallicRoughness.setBaseColorFactor(original);
        }
      } else {
        // Armazenar original e esconder
        originalColors.current.set(material, [...baseColor]);
        material.setAlphaMode("MASK");
        material.pbrMetallicRoughness.setBaseColorFactor([1, 1, 1, 0]);
      }
    }
  }
};


const toggleNormalAttenuation = () => {
  toggleMaterialByColor([0.047, 0.047, 0.8, 0.1]); // Azul
};

const toggleHighAttenuation = () => {
  toggleMaterialByColor([1, 1, 0, 0.2]); // Amarelo
};

const toggleLowAttenuation = () => {
  toggleMaterialByColor([0.702, 0.082, 0.192, 0.5]); // Vermelho
};

const toggleBackgroundColor = () => {
  if (modelViewerRef.current) {
    const close = document.getElementsByClassName('close-modal')[0];
    if (close) {
      close.style.filter = isBlack ? 'invert(1)' : 'invert(0)';
    }
    setIsBlack(!isBlack);
  }
};

  const handleDownload = () => {
    if (!modelBlob) return;

    const downloadUrl = URL.createObjectURL(modelBlob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = 'model.gltf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(downloadUrl);
  };

  if (loading) {
    return (
      <div className="not-available-warning">
        <LoadingSection message={translate('loading3DModel', language)} />
      </div>
    );
  }

  if (modelUnavailable || !modelUrl) {
    return (
      <div
        className="not-available-warning"
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'black',
        }}
      >
        <NoPhotoIcon />
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <div
        style={{
          backgroundColor: '#EEF2FF',
          border: '1px solid #EEF2FF',
          borderRadius: '10px',
          position: 'absolute',
          top: 10,
          left: 10,
          zIndex: 10,
          width: '250px',
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <button
          onClick={toggleHighAttenuation}
          style={{
            backgroundColor: '#FFD700',
            color: '#000',
            border: 'none',
            borderRadius: '8px',
            padding: '10px 16px',
            cursor: 'pointer',
            width: '100%',
            fontWeight: 'bold',
          }}
        >
          {translate('highAttenuation', language)}
        </button>

        <button
          onClick={toggleLowAttenuation}
          style={{
            backgroundColor: '#DC2626',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '10px 16px',
            cursor: 'pointer',
            width: '100%',
            fontWeight: 'bold',
          }}
        >
          {translate('lowAttenuation', language)}
        </button>

        <button
          onClick={toggleNormalAttenuation}
          style={{
            backgroundColor: '#1D4ED8',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '10px 16px',
            cursor: 'pointer',
            width: '100%',
            fontWeight: 'bold',
          }}
        >
          {translate('normalAttenuation', language)}
        </button>

        <button
          onClick={toggleBackgroundColor}
          style={{
            backgroundColor: 'black',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '10px 16px',
            cursor: 'pointer',
            width: '100%',
            fontWeight: 'bold',
          }}
        >
          {translate('background', language)}
        </button>

        <button
          onClick={handleDownload}
          style={{
            backgroundColor: 'white',
            color: '#111',
            borderRadius: '8px',
            padding: '10px 16px',
            cursor: 'pointer',
            width: '100%',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
          title="Baixar modelo"
        >
          <DownloadIcon />
          {translate('donwload', language)}
        </button>
      </div>

      <model-viewer
        ref={modelViewerRef}
        alt="Modelo 3D"
        src={modelUrl}
        camera-controls
        orientation="0deg 90deg auto"
        tone-mapping="neutral"
        shadow-intensity="1"
        style={{ width: '100%', height: '100%', backgroundColor: isBlack ? 'black' : 'white', }}
      >
      </model-viewer>
    </div>
  );
};

ModelViewerComponent.propTypes = {
  report: PropTypes.any,
};

ModelViewerComponent.defaultProps = {
  report: {},
};

export default ModelViewerComponent;
